import React, { useCallback, useEffect, useRef, useState } from 'react';

import 'moment/locale/ru';
import moment from 'moment';
import { Toast } from 'primereact/toast';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'react-calendar-timeline/style.css';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';

import { ImportErrorDialog } from 'ui/shared/ImportErrorDialog';

import { useNewApiCall } from '../../hooks';
import { TreeNode } from '../../types/interfaces';
import '../../ui/css/CalendarAssignments.css';

import { CalendarAssignmentsDialogs } from './CalendarAssignmentsDialogs';
import { CalendarAssignmentsView } from './CalendarAssignmentsView';
import { EntityVisibility } from './EntityVisibility';
import { ExportCalendarAssignmentsDialog } from './ExportCalendarAssignmentsDialog';
import { useCalendarAssignmentsApi } from './hooks/useCalendarAssignmentsApi';
import { useCalendarAssignmentsDialogsState } from './hooks/useCalendarAssignmentsDialogsState';
import { useCalendarAssignmentsInteractions } from './hooks/useCalendarAssignmentsInteractions';
import { useCalendarAssignmentsPersistedState } from './hooks/useCalendarAssignmentsPersistedState';
import { useCalendarAssignmentsQueryParams } from './hooks/useCalendarAssignmentsQueryParams';
import { useCalendarAssignmentsTableControls } from './hooks/useCalendarAssignmentsTableControls';
import { useCalendarAssignmentsTimelineModel } from './hooks/useCalendarAssignmentsTimelineModel';
import { useCalendarAssignmentsTimelineState } from './hooks/useCalendarAssignmentsTimelineState';
import { RuleValidationErrorsDialog } from './RuleValidationErrorsDialog';
import {
  CALENDAR_ASSIGNMENTS_DESKTOP_LAYOUT,
  CalendarAssignmentsTimelineActions,
  CalendarAssignmentsTimelineModel
} from './types';
import { useDataManager } from './useDataManager';

export default function CalendarAssignments() {
  const elementUIs = useSelector((state: RootState) => state.auth.elementUIs);
  const hasTaskRole = elementUIs.some(
    (ui) =>
      (ui.code === 'tasks-add-btn__roles' || ui.code === 'wholeui__roles') &&
      ui.visible
  );

  const {
    viewMode,
    setViewMode,
    visibleEntities,
    setVisibleEntities,
    showOnlyFreeEmployees,
    setShowOnlyFreeEmployees,
    excludeLongAbsences,
    setExcludeLongAbsences,
    todayShiftOnly,
    setTodayShiftOnly,
    sortConfig,
    setSortConfig,
    storedDepartmentFilterRaw: dFilter,
    persistDepartmentFilter
  } = useCalendarAssignmentsPersistedState();

  const [selectedDepartmentFilter, setSelectedDepartmentFilter] =
    useState<TreeNode | null>(null);
  const [selectedDateFilter, setSelectedDateFilter] = useState<Date | null>(
    () => new Date()
  );

  // Обработчик переключения фильтра свободных сотрудников
  const handleToggleFreeEmployeesFilter = useCallback(() => {
    setShowOnlyFreeEmployees((prev) => !prev);
  }, [setShowOnlyFreeEmployees]);

  // Обработчик переключения фильтра длительных отсутствий
  const handleToggleExcludeLongAbsences = useCallback(() => {
    setExcludeLongAbsences((prev) => !prev);
  }, [setExcludeLongAbsences]);

  // Обработчик переключения фильтра работающих сегодня
  const handleToggleTodayShiftOnly = useCallback(() => {
    setTodayShiftOnly((prev) => !prev);
  }, [setTodayShiftOnly]);

  const toast = useRef<Toast>(null);

  const {
    baseDate,
    startOfMonth,
    monthDurationMs,
    timelineScale,
    setTimelineScale,
    highlightedDateForView,
    visibleTimeRange,
    visibleTimeStart,
    visibleTimeEnd,
    selectedItemIds: selectedSchedulesIds,
    selectedItemId: selectedId,
    timelineVersion,
    setTimelineVersion,
    refreshTimeline,
    handleScaleChange,
    handleTimeChange,
    handleSelectItem,
    clearSelectedItems
  } = useCalendarAssignmentsTimelineState({
    selectedDateFilter,
    viewMode
  });

  const {
    calendarCardRef,
    absenceCardRef,
    onHideScheduleCard,
    onHideAbsenceCard,
    openNewDialog,
    selectedTaskInfo,
    setSelectedTaskInfo,
    shiftDialogVisible,
    setShiftDialogVisible,
    shiftData,
    setShiftData,
    absenceDialogVisible,
    setAbsenceDialogVisible,
    absenceData,
    setAbsenceData,
    choiceDialogVisible,
    setChoiceDialogVisible,
    selectedPersonId,
    setSelectedPersonId,
    selectedDate,
    setSelectedDate,
    validationErrors,
    setValidationErrors,
    validationDialogVisible,
    setValidationDialogVisible,
    ruleValidationErrors,
    setRuleValidationErrors,
    ruleValidationDialogVisible,
    setRuleValidationDialogVisible,
    validationErrorsImport,
    setValidationErrorsImport,
    errorDialogVisible,
    setErrorDialogVisible,
    calculatorDialogVisible,
    setCalculatorDialogVisible,
    confirmRecalcDialogVisible,
    setConfirmRecalcDialogVisible,
    recalcParams,
    onCalculatorClick,
    handleCalculatorConfirm,
    deletionDialogVisible,
    setDeletionDialogVisible,
    confirmDeletionDialogVisible,
    setConfirmDeletionDialogVisible,
    deletionParams,
    deletionAbsenceDialogVisible,
    setDeletionAbsenceDialogVisible,
    confirmAbsenceDeletionDialogVisible,
    setConfirmAbsenceDeletionDialogVisible,
    deletionAbsenceParams,
    handleDeleteShiftsClick,
    handleDeleteAbsencesClick,
    handleMassDelete,
    handleMassDeleteAbsences,
    exportDialogVisible,
    setExportDialogVisible,
    handleToggleExportDialogVisible,
    unassignedTasksDialogVisible,
    setUnassignedTasksDialogVisible,
    handleToggleUnassignedTasksDialogVisible,
    unassignedTasksCount,
    setUnassignedTasksCount
  } = useCalendarAssignmentsDialogsState({
    selectedDepartmentFilter,
    selectedDateFilter
  });

  const copyToClipboard = useCallback((text: string, message: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.current?.show({
          severity: 'success',
          summary: 'Скопировано',
          detail: message,
          life: 2000
        });
      })
      .catch((err) => {
        console.error('Ошибка копирования: ', err);
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось скопировать',
          life: 2000
        });
      });
  }, []);

  const {
    filters,
    debouncedFilters,
    onFilterChange,
    onSortChange,
    debouncedHandleSelectionChange,
    handleDateFilterChange
  } = useCalendarAssignmentsTableControls({
    toast,
    sortConfig,
    setSortConfig,
    selectedDateFilter,
    setSelectedDateFilter,
    setTimelineScale,
    persistDepartmentFilter,
    setSelectedDepartmentFilter
  });

  const { dateForQuery, monthDateFilterForUnassigned, apiParams } =
    useCalendarAssignmentsQueryParams({
      selectedDateFilter,
      debouncedFilters,
      sortConfig
    });
  const {
    calendarAssignment,
    departmentSchedules,
    employeesTasks,
    employeesShifts,
    employeesAbsences,
    newAbsences,
    updatedAbsences,
    deletedAbsenceKeys,
    newShifts,
    updatedShifts,
    deletedShiftIds,
    simulatedDeletedShifts,
    simulatedUpdatedShifts,
    setLoading,
    setNewShifts,
    setUpdatedShifts,
    setDeletedShiftIds,
    setHasUnsavedChanges,
    isDepartmentsLoaded,
    treeDropDownValue,
    hasUnsavedChanges,
    handleSaveShift,
    handleDeleteShift,
    handleUpdateAbsence,
    handleDeleteAbsence,
    handleSaveChanges,
    getAssignmentCallback,
    fetchPersonAbsences,
    getDepartmentsCallback,
    employeesTimesheet,
    employeesTotalTimesheet
  } = useDataManager({
    selectedDepartmentFilter,
    selectedDateFilter,
    setTimelineVersion,
    dFilter,
    timelineScale,
    toast,
    apiParams,
    setSelectedDepartmentFilter,
    viewMode,
    highlightedDateForView,
    baseDate,
    showOnlyFreeEmployees,
    excludeLongAbsences,
    todayShiftOnly
  });

  const {
    fetchUnassignedTasksCount,
    handleAfterUnassignedAssign,
    confirmMassDelete,
    confirmMassDeleteAbsences,
    handleRecalculate,
    importAbsencesXLS,
    exportTemplate
  } = useCalendarAssignmentsApi({
    selectedDepartmentFilter,
    unassignedTasksDateFilter: monthDateFilterForUnassigned,
    setUnassignedTasksCount,
    toast,
    setLoading,
    getAssignmentCallback,
    fetchPersonAbsences,
    deletionParams,
    setConfirmDeletionDialogVisible,
    deletionAbsenceParams,
    setConfirmAbsenceDeletionDialogVisible,
    recalcParams,
    setConfirmRecalcDialogVisible,
    setNewShifts,
    setUpdatedShifts,
    setDeletedShiftIds,
    setHasUnsavedChanges,
    setRuleValidationErrors,
    setRuleValidationDialogVisible,
    setValidationErrors,
    setValidationDialogVisible,
    setValidationErrorsImport,
    setErrorDialogVisible
  });

  const {
    groups,
    items: builtTimelineItems,
    sidebarWidth
  } = useCalendarAssignmentsTimelineModel({
    data: {
      calendarAssignment,
      departmentSchedules,
      employeesTasks,
      employeesShifts,
      employeesAbsences,
      employeesTimesheet,
      employeesTotalTimesheet
    },
    changes: {
      newAbsences,
      updatedAbsences,
      deletedAbsenceKeys,
      newShifts,
      updatedShifts,
      deletedShiftIds,
      simulatedDeletedShifts,
      simulatedUpdatedShifts
    },
    view: {
      startOfMonth,
      timelineScale,
      viewMode,
      visibleEntities,
      visibleTimeStart,
      visibleTimeEnd,
      layout: CALENDAR_ASSIGNMENTS_DESKTOP_LAYOUT
    },
    copyToClipboard,
    selectedDepartmentFilter
  });

  const {
    onSaveShift,
    onSaveAbsence,
    onTaskClick,
    onCreateTask,
    handleItemDoubleClick,
    handleAddFromHeader
  } = useCalendarAssignmentsInteractions({
    data: {
      employeesShifts,
      employeesAbsences,
      newAbsences,
      updatedAbsences,
      newShifts,
      updatedShifts,
      timelineItems: builtTimelineItems
    },
    view: {
      viewMode,
      timelineScale,
      selectedDepartmentFilter,
      selectedDateFilter,
      selectedItemId: selectedId
    },
    dialogActions: {
      handleSaveShift,
      handleUpdateAbsence,
      setShiftDialogVisible,
      setAbsenceDialogVisible,
      setSelectedTaskInfo,
      setShiftData,
      setAbsenceData,
      setSelectedPersonId,
      setSelectedDate,
      setChoiceDialogVisible,
      openNewDialog
    }
  });

  const handleApplyVisibility = (entities: EntityVisibility) => {
    setVisibleEntities(entities);
  };

  const handleToggleViewMode = () => {
    setViewMode((prev) => {
      const newMode = prev === 'schedule' ? 'timesheet' : 'schedule';
      return newMode;
    });
    refreshTimeline();
  };

  useNewApiCall(getAssignmentCallback, [
    selectedDepartmentFilter,
    apiParams,
    dateForQuery,
    viewMode,
    showOnlyFreeEmployees,
    excludeLongAbsences,
    todayShiftOnly
  ]);
  useNewApiCall(getDepartmentsCallback, [dFilter]);

  useNewApiCall(fetchUnassignedTasksCount, [
    selectedDepartmentFilter,
    monthDateFilterForUnassigned
  ]);

  useEffect(() => {
    if (!selectedDepartmentFilter?.key) {
      setUnassignedTasksDialogVisible(false);
      setUnassignedTasksCount(0);
    }
  }, [selectedDepartmentFilter, setUnassignedTasksDialogVisible]);

  useEffect(() => {
    if (
      newShifts.length > 0 ||
      updatedShifts.length > 0 ||
      deletedShiftIds.length > 0
    ) {
      refreshTimeline();
    }
  }, [newShifts, updatedShifts, deletedShiftIds, refreshTimeline]);

  const headerProps = {
    importAbsencesXLS,
    exportTemplate,
    selectedDepartmentFilter,
    treeDropDownValue,
    handleSelectionChange: debouncedHandleSelectionChange,
    handleDateFilterChange,
    datepickerDate: selectedDateFilter,
    isDepartmentsLoaded,
    toast,
    hasUnsavedChanges,
    onSaveChanges: handleSaveChanges,
    onAdd: handleAddFromHeader,
    onRecalculateClick: onCalculatorClick,
    onDeleteShiftsClick: handleDeleteShiftsClick,
    onDeleteAbsencesClick: handleDeleteAbsencesClick,
    visibleEntities,
    handleApplyVisibility,
    viewMode,
    onToggleViewMode: handleToggleViewMode,
    onToggleExportDialogVisible: handleToggleExportDialogVisible,
    unassignedTasksCount,
    onOpenUnassignedTasks: handleToggleUnassignedTasksDialogVisible
  };

  const timelineModel: CalendarAssignmentsTimelineModel = {
    startOfMonth,
    daysInMonth: startOfMonth.daysInMonth(),
    groups,
    items: builtTimelineItems,
    timelineScale,
    visibleTimeRange,
    visibleTimeStart,
    visibleTimeEnd,
    monthDurationMs,
    sidebarWidth,
    timelineVersion,
    selectedItemIds: selectedSchedulesIds,
    filters,
    sortBy: sortConfig.sortBy,
    sortDirection: sortConfig.sortDirection,
    showOnlyFreeEmployees,
    excludeLongAbsences,
    todayShiftOnly
  };

  const timelineActions: CalendarAssignmentsTimelineActions = {
    onFilterChange,
    onSortChange,
    onScaleChange: handleScaleChange,
    onTimeChange: handleTimeChange,
    onToggleFreeEmployeesFilter: handleToggleFreeEmployeesFilter,
    onToggleExcludeLongAbsences: handleToggleExcludeLongAbsences,
    onToggleTodayShiftOnly: handleToggleTodayShiftOnly,
    onItemSelect: handleSelectItem,
    onItemDeselect: clearSelectedItems,
    onItemDoubleClick: handleItemDoubleClick
  };

  moment.locale('ru');

  return (
    <>
      <Toast ref={toast} />

      <ImportErrorDialog
        errorDialogVisible={errorDialogVisible}
        setErrorDialogVisible={setErrorDialogVisible}
        validationErrors={validationErrorsImport}
      />

      <CalendarAssignmentsDialogs
        shiftDialogVisible={shiftDialogVisible}
        onHideScheduleCard={onHideScheduleCard}
        selectedDepartmentKey={
          selectedDepartmentFilter ? selectedDepartmentFilter!.key : ''
        }
        shiftData={shiftData}
        selectedDateFilter={selectedDateFilter}
        onSaveShift={onSaveShift}
        handleDeleteShift={handleDeleteShift}
        setShiftDialogVisible={setShiftDialogVisible}
        calendarCardRef={calendarCardRef}
        absenceDialogVisible={absenceDialogVisible}
        onHideAbsenceCard={onHideAbsenceCard}
        selectedId={parseInt(selectedId)}
        absenceData={absenceData}
        employeesShifts={employeesShifts}
        newShifts={newShifts}
        updatedShifts={updatedShifts}
        onSaveAbsence={onSaveAbsence}
        handleDeleteAbsence={handleDeleteAbsence}
        setAbsenceDialogVisible={setAbsenceDialogVisible}
        absenceCardRef={absenceCardRef}
        departmentKey={selectedDepartmentFilter?.key || ''}
        selectedTaskInfo={selectedTaskInfo}
        setSelectedTaskInfo={setSelectedTaskInfo}
        onTaskClick={onTaskClick}
        choiceDialogVisible={choiceDialogVisible}
        setChoiceDialogVisible={setChoiceDialogVisible}
        hasTaskRole={hasTaskRole}
        selectedPersonId={selectedPersonId}
        selectedDate={selectedDate}
        openNewDialog={openNewDialog}
        onCreateTask={onCreateTask}
        calculatorDialogVisible={calculatorDialogVisible}
        setCalculatorDialogVisible={setCalculatorDialogVisible}
        selectedEmployeesOptions={calendarAssignment?.employees || []}
        handleCalculatorConfirm={handleCalculatorConfirm}
        confirmRecalcDialogVisible={confirmRecalcDialogVisible}
        setConfirmRecalcDialogVisible={setConfirmRecalcDialogVisible}
        recalcParams={recalcParams}
        hasUnsavedChanges={hasUnsavedChanges}
        handleRecalculate={handleRecalculate}
        deletionDialogVisible={deletionDialogVisible}
        setDeletionDialogVisible={setDeletionDialogVisible}
        handleMassDelete={handleMassDelete}
        confirmDeletionDialogVisible={confirmDeletionDialogVisible}
        setConfirmDeletionDialogVisible={setConfirmDeletionDialogVisible}
        deletionParams={deletionParams}
        confirmMassDelete={confirmMassDelete}
        deletionAbsenceDialogVisible={deletionAbsenceDialogVisible}
        setDeletionAbsenceDialogVisible={setDeletionAbsenceDialogVisible}
        handleMassDeleteAbsences={handleMassDeleteAbsences}
        confirmAbsenceDeletionDialogVisible={
          confirmAbsenceDeletionDialogVisible
        }
        setConfirmAbsenceDeletionDialogVisible={
          setConfirmAbsenceDeletionDialogVisible
        }
        deletionAbsenceParams={deletionAbsenceParams}
        confirmMassDeleteAbsences={confirmMassDeleteAbsences}
        validationDialogVisible={validationDialogVisible}
        validationErrors={validationErrors}
        onHideValidation={() => setValidationDialogVisible(false)}
        unassignedTasksDialogVisible={unassignedTasksDialogVisible}
        setUnassignedTasksDialogVisible={setUnassignedTasksDialogVisible}
        unassignedTasksDepartmentId={
          selectedDepartmentFilter?.key
            ? Number(selectedDepartmentFilter.key)
            : null
        }
        unassignedTasksDateFilter={monthDateFilterForUnassigned}
        toastRef={toast}
        onAfterUnassignedAssign={handleAfterUnassignedAssign}
      />
      <RuleValidationErrorsDialog
        visible={ruleValidationDialogVisible}
        errors={ruleValidationErrors}
        onHide={() => setRuleValidationDialogVisible(false)}
      />
      <ExportCalendarAssignmentsDialog
        exportDialogVisible={exportDialogVisible}
        onHideExportDialog={() => setExportDialogVisible(false)}
        viewMode={viewMode}
        selectedDateFilter={selectedDateFilter}
        departmentId={
          selectedDepartmentFilter?.key
            ? Number(selectedDepartmentFilter.key)
            : null
        }
      />
      <CalendarAssignmentsView
        isDepartmentSelected={
          !!selectedDepartmentFilter && selectedDepartmentFilter.key !== ''
        }
        headerProps={headerProps}
        timelineModel={timelineModel}
        timelineActions={timelineActions}
        layout={CALENDAR_ASSIGNMENTS_DESKTOP_LAYOUT}
      />
    </>
  );
}
